import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Callout } from '@/components/ui/content';
import { WorkspaceIcon } from '../icons';
import { sizeText, useTimeLabel } from './format';
import type {
  ExportFormat,
  ExportFormatVM,
  ExportJobState,
  ExportPanelVM,
  ExportPlacement,
  ProvenanceVM,
} from './types';

// The Export panel: only the formats this kind has (each row is one button),
// the provenance line every file carries (shown, never switchable), the
// per-Artifact "Add a sources page" choice, and one job's progress. Success is
// only ever drawn from a `ready` job, which the export API sets after it has
// validated the file. Focus order: format rows, then the sources option, then
// the job's actions (Download / Try again).

export interface ExportPanelProps {
  vm: ExportPanelVM;
  onExport?: (format: ExportFormat, options: { sourcesPage: boolean }) => void;
  onRetry?: (format: ExportFormat) => void;
  onSourcesPageChange?: (next: boolean) => void;
}

const FORMAT_KEY = {
  pptx: ['exportFormatPptx', 'exportHintPptx'],
  pdf: ['exportFormatPdf', 'exportHintPdf'],
  docx: ['exportFormatDocx', 'exportHintDocx'],
  xlsx: ['exportFormatXlsx', 'exportHintXlsx'],
  csv: ['exportFormatCsv', 'exportHintCsv'],
} as const;
const PLACEMENT_KEY = {
  footer: 'exportPlacementFooter',
  slide: 'exportPlacementSlide',
  notes: 'exportPlacementNotes',
  about: 'exportPlacementAbout',
  notesAbout: 'exportPlacementNotesAbout',
} as const;

export function ExportPanel({ vm, onExport, onRetry, onSourcesPageChange }: ExportPanelProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const busy = vm.job.status === 'running' || vm.job.status === 'validating';
  const optionFormats = vm.formats.filter((f) => f.available && f.sourcesPage === 'option');
  const deckHasSlide = vm.formats.some((f) => f.sourcesPage === 'deck-has-slide');
  return (
    <section className="ap-panel" aria-labelledby="ap-export-title" data-panel="export">
      <header className="ap-head">
        <h2 id="ap-export-title">{copy.exportTitle}</h2>
        <p>{copy.exportIntro}</p>
      </header>
      <ul className="ap-list">
        {vm.formats.map((format) => (
          <FormatRow
            key={format.id}
            format={format}
            disabled={busy || !format.available}
            onExport={() =>
              onExport?.(format.id, {
                sourcesPage: format.sourcesPage === 'option' && vm.sourcesPage,
              })
            }
          />
        ))}
      </ul>
      {deckHasSlide && optionFormats.length === 0 && (
        <p className="ap-note">{copy.exportSourcesPageDeck}</p>
      )}
      {optionFormats.length > 0 && (
        <label className="ap-option-row">
          <input
            type="checkbox"
            checked={vm.sourcesPage}
            onChange={(event) => onSourcesPageChange?.(event.currentTarget.checked)}
          />
          <span className="ap-row-copy">
            <strong>{copy.exportSourcesPage}</strong>
            <span>{copy.exportSourcesPageHint}</span>
            <span>{copy.exportSourcesPageSaved}</span>
          </span>
        </label>
      )}
      <Job job={vm.job} onRetry={onRetry} />
      <Provenance provenance={vm.provenance} formats={vm.formats} />
    </section>
  );
}

function FormatRow({
  format,
  disabled,
  onExport,
}: {
  format: ExportFormatVM;
  disabled: boolean;
  onExport: () => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const [name, hint] = FORMAT_KEY[format.id];
  return (
    <li>
      <button
        type="button"
        className="ap-row ap-format"
        data-format={format.id}
        disabled={disabled}
        onClick={onExport}
      >
        <span className="ap-row-copy">
          <strong>{copy[name]}</strong>
          <span>{format.available ? copy[hint] : copy.exportUnavailable}</span>
        </span>
        <WorkspaceIcon name="download" size={16} />
      </button>
    </li>
  );
}

function Job({ job, onRetry }: { job: ExportJobState; onRetry?: (format: ExportFormat) => void }) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  if (job.status === 'idle') return null;
  const format = (id: ExportFormat) => copy[FORMAT_KEY[id][0]];
  if (job.status === 'running')
    return (
      <div className="ap-job" role="status">
        <p>{formatCopy(copy.exportRunning, { format: format(job.format) })}</p>
        <progress
          max={100}
          {...(job.progress === null ? {} : { value: Math.round(job.progress * 100) })}
        />
      </div>
    );
  if (job.status === 'validating')
    return (
      <div className="ap-job" role="status">
        <p>{formatCopy(copy.exportValidating, { format: format(job.format) })}</p>
      </div>
    );
  if (job.status === 'ready')
    return (
      <div className="ap-job" role="status" data-state="ready">
        <p>
          <strong>{formatCopy(copy.exportReady, { format: format(job.format) })}</strong>{' '}
          {formatCopy(copy.exportVersion, { number: String(job.version) })} ·{' '}
          {sizeText(job.sizeBytes)}
        </p>
        {job.files.map((file, index) =>
          index === 0 ? (
            <a
              key={file.id}
              className="ws-button"
              data-file-id={file.id}
              href={file.url}
              download={file.downloadName}
            >
              <WorkspaceIcon name="download" size={14} />{' '}
              {formatCopy(copy.exportDownload, { name: file.downloadName })}
            </a>
          ) : (
            // Every later file (the provenance companion) is a quiet link under the primary one;
            // its real file name is still the download name, never a clipped label.
            <a
              key={file.id}
              className="ap-about-file"
              data-file-id={file.id}
              href={file.url}
              download={file.downloadName}
              title={file.downloadName}
            >
              {formatCopy(copy.exportAboutFile, {
                format: job.format === 'csv' ? 'CSV' : 'JSON',
              })}
            </a>
          ),
        )}
      </div>
    );
  const message =
    job.reason === 'denied'
      ? copy.exportDenied
      : job.reason === 'revoked'
        ? copy.exportRevoked
        : job.reason === 'stale'
          ? copy.exportStale
          : job.reason === 'checks'
            ? formatCopy(copy.exportFailedChecks, { format: format(job.format) })
            : formatCopy(copy.exportFailed, { format: format(job.format) });
  return (
    <Callout
      tone={job.reason === 'stale' ? 'warning' : 'danger'}
      actions={
        job.retryable ? (
          <button type="button" className="ws-button" onClick={() => onRetry?.(job.format)}>
            {copy.retry}
          </button>
        ) : undefined
      }
    >
      {message}
    </Callout>
  );
}

/** The one line every file carries; the data part is as of / from-to / unknown. */
export function provenanceText(
  copy: Readonly<Record<string, string>>,
  p: ProvenanceVM,
  time: (value: string | null | undefined) => string | null,
): string {
  const from = time(p.dataFrom);
  const to = time(p.dataTo);
  const data =
    from && to
      ? from === to
        ? formatCopy(copy.exportProvenanceAsOf, { time: from })
        : formatCopy(copy.exportProvenanceRange, { earliest: from, latest: to })
      : copy.exportProvenanceUnknown;
  return formatCopy(copy.exportProvenanceLine, {
    title: p.title,
    number: String(p.version),
    data,
    madeAt: time(p.madeAt) ?? '',
  });
}

/** Placements for the formats offered only; XLSX and CSV share one line. */
function placementsFor(formats: ExportFormatVM[]): (ExportPlacement | 'notesAbout')[] {
  const set = new Set(formats.map((f) => f.placement));
  if (set.has('notes') && set.has('about')) {
    set.delete('notes');
    set.delete('about');
    return [...set, 'notesAbout'];
  }
  return [...set];
}

function Provenance({
  provenance,
  formats,
}: {
  provenance: ProvenanceVM;
  formats: ExportFormatVM[];
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const time = useTimeLabel();
  return (
    <div className="ap-provenance">
      <h3>{copy.exportProvenanceTitle}</h3>
      <p>{provenance.minimumLine ?? provenanceText(copy, provenance, time)}</p>
      <ul>
        {placementsFor(formats).map((p) => (
          <li key={p}>{copy[PLACEMENT_KEY[p]]}</li>
        ))}
      </ul>
    </div>
  );
}
